import { useState } from "react";
import { motion } from "framer-motion";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { downloadFile, errMsg, fmtDate } from "@/lib/api";
import { useApi, Skeleton, inputCls, btnPrimary } from "@/components/portal/ui";
import { EASE } from "@/components/motion";
import { AdminTitle } from "@/pages/admin/AdminLayout";

const thisMonth = () => new Date().toISOString().slice(0, 7);
const monthLabel = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString("id-ID", { month: "short", year: "2-digit" });

function Bar({ value, max, label, sub, i }) {
  return (
    <div data-testid={`report-schedule-${i}`}>
      <div className="flex items-baseline justify-between text-sm"><span className="font-bold text-slate-900">{label}</span><span className="text-slate-500 tabular-nums">{value} · {sub} unik</span></div>
      <div className="mt-2 h-3 rounded-full bg-slate-100 overflow-hidden">
        <motion.div initial={{ width: 0 }} animate={{ width: `${max ? (value / max) * 100 : 0}%` }} transition={{ duration: 1, delay: i * 0.08, ease: EASE }} className="h-full rounded-full bg-blue-600" />
      </div>
    </div>
  );
}

function Trend({ monthly, active }) {
  const max = Math.max(1, ...monthly.map((m) => m.total));
  return (
    <div className="flex items-end gap-3 h-44" data-testid="report-trend">
      {monthly.map((m, i) => (
        <div key={m.month} className="flex-1 flex flex-col items-center gap-2 h-full justify-end">
          <span className="text-xs font-bold text-slate-900 tabular-nums">{m.total}</span>
          <motion.div initial={{ height: 0 }} animate={{ height: `${(m.total / max) * 100}%` }} transition={{ duration: 0.9, delay: i * 0.06, ease: EASE }} className={`w-full rounded-xl min-h-[4px] ${m.month === active ? "bg-blue-600" : "bg-slate-200"}`} />
          <span className="text-[11px] font-semibold text-slate-500">{monthLabel(m.month)}</span>
        </div>
      ))}
    </div>
  );
}

export default function Reports() {
  const [month, setMonth] = useState(thisMonth());
  const { data } = useApi(`/admin/reports/attendance?month=${month}`);
  const [busy, setBusy] = useState(false);

  const exportXlsx = async () => {
    setBusy(true);
    try {
      await downloadFile(`/admin/reports/attendance.xlsx?month=${month}`, `kehadiran-${month}.xlsx`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const max = data ? Math.max(1, ...data.per_schedule.map((p) => p.count)) : 1;
  const stats = data ? [["Total Check-in", data.total], ["Jemaat Unik", data.unique_members], ["Ibadah Tercatat", data.services], ["Rata-rata / Ibadah", data.avg_per_service]] : [];

  return (
    <div data-testid="admin-reports">
      <AdminTitle eyebrow="Laporan" title="Kehadiran Ibadah">
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value || thisMonth())} className={`${inputCls} !w-auto !py-3`} data-testid="report-month-input" />
        <button onClick={exportXlsx} disabled={busy} className={btnPrimary} data-testid="report-export-button">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}Unduh Excel</button>
      </AdminTitle>
      {!data ? <Skeleton className="h-96" /> : (
        <>
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
            {stats.map(([l, v], i) => (
              <div key={l} className={`rounded-3xl p-6 border border-slate-100 shadow-sm ${i === 0 ? "bg-blue-600 text-white" : "bg-white"}`} data-testid={`report-stat-${i}`}>
                <p className="text-3xl font-black tabular-nums">{v}</p><p className="text-sm opacity-70 mt-1">{l}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 grid lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-3xl p-6 md:p-8 shadow-sm border border-slate-100 space-y-5">
              <p className="font-extrabold text-slate-900">Per Ibadah · {monthLabel(month)}</p>
              {data.per_schedule.map((p, i) => <Bar key={p.schedule_id} i={i} value={p.count} sub={p.unique} max={max} label={p.title} />)}
            </div>
            <div className="bg-white rounded-3xl p-6 md:p-8 shadow-sm border border-slate-100">
              <p className="font-extrabold text-slate-900 mb-6">Tren 6 Bulan</p>
              <Trend monthly={data.monthly} active={month} />
            </div>
          </div>
          <div className="mt-6 bg-white rounded-3xl shadow-sm border border-slate-100 overflow-x-auto">
            <table className="w-full text-sm" data-testid="report-date-table">
              <thead className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
                <tr><th className="px-5 py-4 font-bold">Tanggal</th>{data.schedules.map((s) => <th key={s.id} className="px-5 py-4 font-bold">{s.title}</th>)}<th className="px-5 py-4 font-bold">Total</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.per_date.map((r) => (
                  <tr key={r.date}><td className="px-5 py-4 font-semibold text-slate-900">{fmtDate(r.date, { weekday: "short", day: "numeric", month: "short" })}</td>
                    {data.schedules.map((s) => <td key={s.id} className="px-5 py-4 tabular-nums text-slate-600">{r.by_schedule[s.id] || 0}</td>)}
                    <td className="px-5 py-4 font-black tabular-nums">{r.total}</td></tr>
                ))}
              </tbody>
            </table>
            {data.per_date.length === 0 && <p className="p-8 text-center text-slate-500" data-testid="report-empty">Belum ada check-in di bulan ini.</p>}
          </div>
        </>
      )}
    </div>
  );
}
